/**
 * Centralised SEO constants and helpers (Phase 1 — technical foundation).
 *
 * Every public route builds its canonical URL and `og:url` from here so the
 * site advertises one consistent origin (the production custom domain).
 */
export const SITE_URL = "https://cb16.in";
export const SITE_NAME = "CB16 Atelier";

/**
 * Percent-encode path segments so slugs containing spaces or other unsafe
 * characters emit valid absolute URLs (slug data itself is never changed).
 */
export function encodePath(path: string): string {
  return path
    .split("/")
    .map((segment) => (segment ? encodeURIComponent(decodeURIComponent(segment)) : segment))
    .join("/");
}

/** Absolute URL for a site-relative path, without trailing-slash duplicates. */
export function canonicalUrl(path: string): string {
  if (!path || path === "/") return `${SITE_URL}/`;
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const trimmed = withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : withSlash;
  return `${SITE_URL}${encodePath(trimmed)}`;
}


/** Absolute URL for an image that may be site-relative already. */
export function absoluteImage(src: string | null | undefined): string | undefined {
  if (!src) return undefined;
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE_URL}${src.startsWith("/") ? src : `/${src}`}`;
}

/** Canonical link + `og:url` pair for a public, indexable page. */
export function canonicalTags(path: string) {
  const url = canonicalUrl(path);
  return {
    meta: [{ property: "og:url", content: url }],
    links: [{ rel: "canonical" as const, href: url }],
  };
}

/** Robots directive for private/application pages. */
export const NOINDEX = { name: "robots", content: "noindex, nofollow" } as const;
/** Explicit directive for public commercial/content pages. */
export const INDEX_FOLLOW = { name: "robots", content: "index, follow" } as const;
